<!--
  WilcomEmbRenderStage.vue -- SA>Developer .emb/.dst render test stage.

  DURABLE DEV TOOL, not a throwaway prototype. Upload an .emb or .dst design
  file and render it through the SAME render service the artwork preview
  drain uses (render_emb_via_wilcom for .emb -- the PAID Wilcom EWA path;
  render_dst for .dst -- the FREE local pyembroidery path). Shows the
  thumbnail + full-size render side by side, plus stitch count and the
  request/response recipe that was actually sent to Wilcom -- a real
  debugging tool for verifying a render without touching the drain's
  paid-call cache.

  Backend: POST /api/wilcom/design-render-stage (multipart/form-data,
  field "file"). Gated superadmin.settings, same tier as the sibling
  Wilcom Lettering prototype.

  Root-cause context (2026-08-02): .emb renders were coming back as pale,
  unfilled ghost outlines because the recipe used colorwaytype="all" with
  no colorway to resolve thread colors against. The fix (colorwaytype=
  "current") is oracle-verified against Mike's Wilcom True Preview harness
  -- see api/src/services/wilcom_emb_render.py for the full rationale. This
  stage exists so that rationale can be re-verified by hand against any
  .emb/.dst file, at any time, without writing a script.
-->
<template>
  <div class="emb-stage" data-testid="wilcom-emb-render-stage">
    <header class="emb-stage__head">
      <h1 class="emb-stage__title">Wilcom .emb / .dst render stage</h1>
      <p class="emb-stage__sub">
        Upload an embroidery design file and render it through the exact
        service the artwork preview drain uses. ``.emb`` renders via the
        paid Wilcom EWA call (colorwaytype="current", oracle-verified);
        ``.dst`` renders locally for free. Use this to confirm a render
        looks right before trusting the drain's cached output.
      </p>
    </header>

    <div class="emb-stage__field">
      <label class="emb-stage__label" for="emb-stage-upload">
        Design file (.emb or .dst)
      </label>
      <input
        id="emb-stage-upload"
        type="file"
        accept=".emb,.dst"
        class="emb-stage__file-input"
        data-testid="dev-emb-stage-upload"
        @change="onFileChange"
      />
      <p v-if="status === 'rendering'" class="emb-stage__hint">
        Rendering...
      </p>
    </div>

    <p
      v-if="error"
      class="emb-stage__error"
      data-testid="dev-emb-stage-error"
    >
      {{ error }}
    </p>

    <div v-if="result" class="emb-stage__result">
      <div class="emb-stage__images">
        <figure class="emb-stage__figure">
          <figcaption>Thumbnail</figcaption>
          <img
            :src="result.thumbnail_url"
            alt="Rendered design thumbnail"
            class="emb-stage__thumb"
            data-testid="dev-emb-thumbnail"
          />
        </figure>
        <figure class="emb-stage__figure">
          <figcaption>Full-size</figcaption>
          <img
            :src="result.fullsize_url"
            alt="Rendered design full-size"
            class="emb-stage__full"
            data-testid="dev-emb-fullsize"
          />
        </figure>
      </div>

      <dl class="emb-stage__meta">
        <div class="emb-stage__meta-row">
          <dt>Filename</dt>
          <dd>{{ result.filename }}</dd>
        </div>
        <div class="emb-stage__meta-row">
          <dt>Source</dt>
          <dd>{{ result.source }}</dd>
        </div>
        <div class="emb-stage__meta-row">
          <dt>Stitch count</dt>
          <dd data-testid="dev-emb-stitchcount">{{ result.stitch_count }}</dd>
        </div>
        <div class="emb-stage__meta-row">
          <dt>Size (in)</dt>
          <dd>{{ formatBbox(result.bbox_in) }}</dd>
        </div>
      </dl>

      <div
        v-if="result.request_summary || result.response_summary"
        class="emb-stage__summary"
        data-testid="dev-emb-request-summary"
      >
        <h2 class="emb-stage__summary-title">Request / response summary</h2>
        <pre class="emb-stage__summary-body">{{ summaryText }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import {
  ARTWORK_UPLOAD_ERROR_EMPTY,
  isEmptyFile,
} from '@/utils/artworkUploadContract'

const SUPPORTED_EXTENSIONS = ['.emb', '.dst']

function extOf(filename) {
  const idx = (filename || '').lastIndexOf('.')
  return idx === -1 ? '' : filename.slice(idx).toLowerCase()
}

export default {
  name: 'WilcomEmbRenderStage',
  data() {
    return {
      status: 'idle',
      error: null,
      result: null,
    }
  },
  computed: {
    summaryText() {
      const parts = {}
      if (this.result?.request_summary) {
        parts.request = this.result.request_summary
      }
      if (this.result?.response_summary) {
        parts.response = this.result.response_summary
      }
      return JSON.stringify(parts, null, 2)
    },
  },
  methods: {
    async onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      this.error = null
      this.result = null

      // Before the extension check: a placeholder .dst/.emb is a
      // supported type carrying zero bytes, so reporting the type
      // would be a confidently wrong cause.
      if (isEmptyFile(file)) {
        this.error = ARTWORK_UPLOAD_ERROR_EMPTY
        this.status = 'idle'
        return
      }

      const ext = extOf(file.name)
      if (!SUPPORTED_EXTENSIONS.includes(ext)) {
        this.error = `Unsupported file type "${ext}" -- only .emb or .dst accepted`
        return
      }

      this.status = 'rendering'
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/wilcom/design-render-stage', formData, {
        })
        this.result = res.data
        this.status = 'done'
      } catch (e) {
        this.error = this.errText(e)
        this.status = 'error'
      }
    },
    errText(e) {
      return (
        e?.response?.data?.error?.message || e?.message || 'Request failed'
      )
    },
    formatBbox(bbox) {
      if (!Array.isArray(bbox) || bbox.length !== 2) return 'unknown'
      const [w, h] = bbox
      return `${w} x ${h}`
    },
  },
}
</script>

<style scoped>
.emb-stage {
  max-width: 980px;
}
.emb-stage__title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  color: #1a202c;
}
.emb-stage__sub {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 24px;
  max-width: 70ch;
}
.emb-stage__field {
  margin-bottom: 16px;
}
.emb-stage__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #4a5568;
  margin-bottom: 6px;
}
.emb-stage__file-input {
  display: block;
  font-size: 14px;
}
.emb-stage__hint {
  font-size: 12px;
  color: var(--tf-fg-4);
  margin: 8px 0 0;
}
.emb-stage__error {
  color: #c53030;
  font-size: 13px;
  margin: 10px 0;
}
.emb-stage__images {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.emb-stage__figure {
  margin: 0;
  background: #f7fafc;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}
.emb-stage__figure figcaption {
  font-size: 12px;
  color: var(--tf-fg-4);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.emb-stage__thumb {
  max-width: 160px;
  max-height: 160px;
}
.emb-stage__full {
  max-width: 480px;
  max-height: 480px;
}
.emb-stage__meta {
  margin: 20px 0 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.emb-stage__meta-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 14px;
  font-size: 13px;
  border-bottom: 1px solid #edf2f7;
}
.emb-stage__meta-row:last-child {
  border-bottom: none;
}
.emb-stage__meta-row dt {
  color: var(--tf-fg-3);
  margin: 0;
}
.emb-stage__meta-row dd {
  color: #1a202c;
  font-weight: 600;
  margin: 0;
}
.emb-stage__summary {
  margin-top: 20px;
}
.emb-stage__summary-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px;
}
.emb-stage__summary-body {
  background: #1a202c;
  color: #e2e8f0;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 12px;
  overflow-x: auto;
}
</style>
